import { computed, ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { IRogue } from '@/js/services/RogueTypes'
import { APPLEPAY_SUPPORT_APP_VERSION } from '@/js/app-bridge/applepay'
import { useCustomerStore } from '@/js/stores/customerStore'  

export type Region = {
  id: number,
  label: string,
  value: string
}

export type Country = {
  name: string,
  alpha2: string,
  requireRegion?: boolean,
  postcodeRegex?: string
  stateLabel?: string | boolean
  zipLabel?: string
}

export type ConfigStoreState = {
  applePay: boolean,
  countries: Country[],
  regions: { [key: string]: Region[] }
}

export const useConfigStore = defineStore('config', () => {
  const COUNTRY_LS_KEY = 'r_cnt_v2'
  const REGION_LS_KEY = 'r_rgn_v2'

  const customerStore = useCustomerStore()

  let countryPromise: Promise<void>

  const applePay = ref(false)
  const googlePay = ref(false)
  const countries = ref<ConfigStoreState['countries']>([])
  const regions = ref<ConfigStoreState['regions']>({})

  /** Built once per country-list change so callers do not re-scan the array per lookup */
  const countryByCode = computed(() => new Map(countries.value.map(c => [c.alpha2, c])))

  /**
   * Get the default address for this user / site
   * @returns Address
   */
  function defaultAddress(rogue: IRogue) {
    return {
      firstname : customerStore.firstname || '',
      lastname  : customerStore.lastname || '',
      email     : customerStore.email || '',
      company   : '',
      city      : '',
      street    : [],
      apartment : '',
      region    : '',
      regionId  : '',
      postcode  : '',
      countryId : rogue.l10n.country || rogue.site.defaultCountry,
      telephone : customerStore.telephone || '',
    }
  }

  function paypalScriptUrl(rogue: IRogue) {
    const apiParams: { [key: string]: string } = {
      'client-id'       : rogue.keys.paypal,
      'disable-funding' : 'card',
      components        : 'buttons,marks,funding-eligibility',
      currency          : rogue.site.currency,
      intent            : 'capture',
      commit            : 'false',
    }

    const params = Object.keys(apiParams)
      .map(key => `${key}=${encodeURIComponent(apiParams[key])}`)
      .join('&')

    return `https://www.paypal.com/sdk/js?${params}`
  }

  function setRegions(regionData: ConfigStoreState['regions'], rogue: IRogue) {
    regions.value = regionData

    if (!rogue.ssr && Object.keys(regionData || {}).length) {
      try {
        window.localStorage.setItem(REGION_LS_KEY, JSON.stringify(regionData))
      } catch (e) {
        //
      }
    }
  }

  function setCountries(countryData: Country[], rogue: IRogue) {
    if (countryData && countryData.length) {
      while (countries.value.length) {
        countries.value.pop()
      }

      if (!rogue.ssr) {
        try {
          countries.value.push(...countryData)
          window.localStorage.setItem(COUNTRY_LS_KEY, JSON.stringify(countryData))
        } catch (e) {
          //
        }
      }
    }
  }

  function init(rogue: IRogue) {
    if (!rogue.ssr) {
      try {
        const storedCountries = JSON.parse(window.localStorage.getItem(COUNTRY_LS_KEY) || '[]')
        if (storedCountries && storedCountries.length) {
          setCountries(storedCountries, rogue)
        }
        const storedRegions = JSON.parse(window.localStorage.getItem(REGION_LS_KEY) || '{}')
        if (storedRegions && Object.keys(storedRegions).length > 0) {
          setRegions(storedRegions, rogue)
        }
      } catch (e) {
        //
      }
    }
  }

  function checkApplePay(rogue: IRogue) {
    if ((rogue.site.applePay?.supported || rogue.adyenCheckout) && !rogue.ssr) {
      try {
        // Allow applepay for all browsers if adyen is enabled
        // canMakePayments throws if served insecurely (HTTP)
        const canMakeWebPayments = !rogue.appContext && ((rogue.adyenCheckout && rogue.featureFlags.isOn('enable_applepay_3rd_party_browsers')) || window.ApplePaySession?.canMakePayments())
        const canMakeAppPayments = rogue.appContext?.platform === 'ios' && rogue.featureFlags.eval('app_applepay') !== 'off'
          && rogue.isApp(APPLEPAY_SUPPORT_APP_VERSION)

        if (canMakeWebPayments || canMakeAppPayments) {
          applePay.value = true
        }
      } catch (e) {
        console.error('Failed to determine Apple Pay Status', e)
      }
    }
  }

  function checkGooglePay(rogue: IRogue) {
    if ((rogue.site.googlePay?.supported || rogue.adyenCheckout) && !rogue.ssr && !rogue.featureFlags.isOn('disable_googlepay')) {
      const ua = navigator.userAgent
      const isDesktopSafari = !!(window as any).safari
      const isMobileSafari = /iP(ad|hone|od)/i.test(ua) && /WebKit/i.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua)

      const canMakeWebPayments = !rogue.appContext && !isDesktopSafari && !isMobileSafari
      const canMakeAppPayments = rogue.appContext?.platform === 'android' && rogue.featureFlags.eval('app_googlepay') !== 'off'

      if (canMakeWebPayments || canMakeAppPayments) {
        googlePay.value = true
      }
    }
  }

  function loadCountries(rogue: IRogue) {
    if (!countryPromise && !countries.value.length) {
      countryPromise = rogue.fetch<Country[]>('/svc_ssr/countries')
        .then(data => setCountries(data, rogue))
        .catch(() => {
          // Robots may hit this if the endpoint is not accessible
        })
    }

    return countryPromise
  }

  async function loadRegions(countryCode: string, rogue: IRogue) {
    if (regions.value[countryCode] || !countryCode) {
      return
    }

    loadCountries(rogue)

    const country = countries.value.find(c => c.alpha2 === countryCode)

    if (
      country?.requireRegion === undefined
      && countryCode !== 'APO'
    ) {
      return
    }

    const data = await rogue.fetch<Region[]>(`/svc_ssr/regions?countryCode=${countryCode}`)
    setRegions({ ...regions.value, [countryCode]: data }, rogue)
  }

  return {
    applePay,
    countries,
    countryByCode,
    googlePay,
    regions,
    defaultAddress,
    paypalScriptUrl,
    init,
    checkApplePay,
    checkGooglePay,
    loadRegions,
    loadCountries,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useConfigStore, import.meta.hot))
}
